import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'actions';
import { setApplication, setIsBorrowerManualFlow, setSessionId } from 'actions/application';
import { setBorrower } from 'actions/borrower';
import { setCoBorrower } from 'actions/coBorrower';
import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { setProveErrorInfo } from 'actions/proveErrorInfo';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import Loader from 'components/Loader';
import Select from 'components/Select';
import { IS_PROVE_PREFILL_ENABLED, NOT_LATIN_ALPHABET_REG_EX } from 'constants/constants';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { LoanPurpose } from 'enums/LoanPurpose';
import { ProveAuthType } from 'enums/ProveAuthType';
import { SwimmingPoolType } from 'enums/SwimmingPoolType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { AppRoutes } from 'routes/routes.enums';
import { getBorrowerShortInfo } from 'thunks';
import { checkBorrower } from 'utils/checkBorrower';
import { checkBirthDate } from 'utils/dateUtils';
import { OptionType } from 'utils/inputSelect';
import { registerFields } from 'utils/registrationUtils';
import { getErrorMessageForRequiredFields, removeNonDigits, removeSpaces } from 'utils/stringUtils';
import { isOnlyLatinAlphabet, isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { MIN_LOAN_FOR_PORTAL } from 'pages/PreQualification/steps/LoanAmount/loanAmount.constants';
import { PhoneLineType, proveApi } from 'api/ProveApi';
import { ButtonType } from 'enums/ButtonType';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { applicationApi } from 'api/ApplicationApi';

import {
  EMAIL_CONFIRMATION,
  INVALID_PHONE_LINE_TYPE,
  LOAN_TYPE_CAPTION,
  OTHER_HOME_IMPROVEMENT_CAPTION,
} from './loanInformation.constants';
import styles from './LoanInformation.module.scss';

const ApplicationTypeOptions: OptionType[] = [
  {
    label: 'Individual Application',
    value: ApplicationType.Individual,
  },
  {
    label: 'Joint Application',
    value: ApplicationType.Joint,
  },
];

const getSelectOptions = (selectOptionsType: { [key: string]: string }): OptionType[] => {
  const selectOptions = [];

  for (let optionKey in selectOptionsType) {
    selectOptions.push({
      label: selectOptionsType[optionKey],
      value: selectOptionsType[optionKey],
    });
  }

  return selectOptions;
};

const LoanInformation = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const application = useAppSelector((state) => state.application);
  const borrowerState = useAppSelector((state) => state.borrower);
  const coBorrowerState = useAppSelector((state) => state.coBorrower);
  const { isBorrowerAuthorized } = useAppSelector((state) => state.prequalificationPortal);

  const { confirmBorrowerEmail, confirmCoborrowerEmail, isNeedToClearLoanInformation } = useAppSelector(
    (state) => state.prequalificationPortal,
  );
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const isPosLoanAmount =
    application?.[LoanInformationVariables.LoanAmount] &&
    (application?.[LoanInformationVariables.LoanAmount] as number) < MIN_LOAN_FOR_PORTAL;
  const defaultLoanType =
    application[LoanInformationVariables.LoanType] || (isPosLoanAmount ? ApplicationType.Individual : null);

  const {
    register,
    unregister,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    control,
    clearErrors,
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      [LoanInformationVariables.LoanType]: defaultLoanType,
      [LoanInformationVariables.LoanPurpose]: application[LoanInformationVariables.LoanPurpose],
      [LoanInformationVariables.SwimmingPoolType]: application[LoanInformationVariables.SwimmingPoolType],
      [LoanInformationVariables.OtherHomeImprovementType]:
        application[LoanInformationVariables.OtherHomeImprovementType],
      [LoanInformationVariables.BorrowerCellPhone]: borrowerState.phone,
      [LoanInformationVariables.BorrowerDateOfBirth]: borrowerState.dateOfBirth,
      [LoanInformationVariables.BorrowerEmail]: borrowerState.email,
      [LoanInformationVariables.ConfirmBorrowerEmail]: confirmBorrowerEmail,
      [LoanInformationVariables.CoBorrowerCellPhone]: coBorrowerState.phone,
      [LoanInformationVariables.CoBorrowerDateOfBirth]: coBorrowerState.dateOfBirth,
      [LoanInformationVariables.CoBorrowerEmail]: coBorrowerState.email,
      [LoanInformationVariables.ConfirmCoBorrowerEmail]: confirmCoborrowerEmail,
    },
  });

  const watcher = watch();

  const isBorrowerEmail = (value: string) => {
    return (
      value?.toLowerCase() !== watcher[LoanInformationVariables.BorrowerEmail] ||
      'Must be different from Borrower Email'
    );
  };

  useEffect(() => {
    register(LoanInformationVariables.LoanType, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanType),
    });
    register(LoanInformationVariables.LoanPurpose, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanPurpose),
    });
    register(LoanInformationVariables.BorrowerCellPhone, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerCellPhone),
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register(LoanInformationVariables.BorrowerDateOfBirth, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerDateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(LoanInformationVariables.BorrowerEmail, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerEmail),
      validate: (value) => {
        return isValidEmail(value) || 'Please enter a valid email address';
      },
    });
    register(LoanInformationVariables.ConfirmBorrowerEmail, {
      required: !isBorrowerAuthorized && getErrorMessageForRequiredFields(EMAIL_CONFIRMATION),
      validate: (value) =>
        !!isBorrowerAuthorized ||
        !watcher[LoanInformationVariables.BorrowerEmail] ||
        !watcher[LoanInformationVariables.ConfirmBorrowerEmail] ||
        value?.toLowerCase() === watcher[LoanInformationVariables.BorrowerEmail] ||
        'Entered Email does not match Borrower Email',
    });

    const isJoint = watcher[LoanInformationVariables.LoanType] === ApplicationType.Joint;

    registerFields(register, [
      {
        name: LoanInformationVariables.CoBorrowerCellPhone,
        required: isJoint,
        label: LoanInformationVariables.CoBorrowerCellPhone,
        customValidation: (value) => {
          if (watcher[LoanInformationVariables.BorrowerCellPhone] === value) {
            return 'Must be different from Borrower Cell Phone';
          }

          return validatePhoneNumber(value);
        },
      },
      {
        name: LoanInformationVariables.CoBorrowerDateOfBirth,
        required: isJoint,
        label: LoanInformationVariables.CoBorrowerDateOfBirth,
        customValidation: (value) => checkBirthDate(value) || 'Please enter a valid date',
      },
      {
        name: LoanInformationVariables.CoBorrowerEmail,
        required: isJoint,
        label: LoanInformationVariables.CoBorrowerEmail,
        validationErrorMessage: 'Please enter a valid email address',
        customValidation: (value: string) => {
          return isValidEmail(value) ? isBorrowerEmail(value) : 'Please enter a valid email address';
        },
      },
      {
        name: LoanInformationVariables.ConfirmCoBorrowerEmail,
        required: isJoint,
        label: EMAIL_CONFIRMATION,
        customValidation: (value) =>
          !watcher[LoanInformationVariables.CoBorrowerEmail] ||
          !watcher[LoanInformationVariables.ConfirmCoBorrowerEmail] ||
          value?.toLowerCase() === watcher[LoanInformationVariables.CoBorrowerEmail] ||
          'Entered Email does not match Co-borrower Email',
      },
    ]);
  }, [register, unregister, watcher]);

  useEffect(() => {
    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement) {
      register(LoanInformationVariables.OtherHomeImprovementType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.OtherHomeImprovementType),
        validate: (value) =>
          isOnlyLatinAlphabet(value) || 'Please enter a valid Type. Type can only contain Latin letters',
      });
    }

    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool) {
      register(LoanInformationVariables.SwimmingPoolType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.SwimmingPoolType),
      });
    }

    return () => {
      unregister(LoanInformationVariables.OtherHomeImprovementType);
      unregister(LoanInformationVariables.SwimmingPoolType);
    };
  }, [register, unregister, watcher[LoanInformationVariables.LoanPurpose]]);

  useEffect(() => {
    if (isNeedToClearLoanInformation) {
      reset({});
      dispatch(setPrequalificationPortal({ isNeedToClearLoanInformation: false }));
    }
  }, [dispatch, reset, isNeedToClearLoanInformation]);

  const saveInformationToStore = () => {
    dispatch(
      setApplication({
        [LoanInformationVariables.LoanType]: watcher[LoanInformationVariables.LoanType] as ApplicationType,
        [LoanInformationVariables.LoanPurpose]: watcher[LoanInformationVariables.LoanPurpose] as LoanPurpose,
        [LoanInformationVariables.OtherHomeImprovementType]: watcher[LoanInformationVariables.OtherHomeImprovementType],
        [LoanInformationVariables.SwimmingPoolType]: watcher[
          LoanInformationVariables.SwimmingPoolType
        ] as SwimmingPoolType,
      }),
    );

    dispatch(
      setPrequalificationPortal({
        confirmBorrowerEmail: watcher[LoanInformationVariables.ConfirmBorrowerEmail],
        confirmCoborrowerEmail: watcher[LoanInformationVariables.ConfirmCoBorrowerEmail],
      }),
    );

    dispatch(
      setBorrower({
        email: watcher[LoanInformationVariables.BorrowerEmail],
        phone: watcher[LoanInformationVariables.BorrowerCellPhone],
        dateOfBirth: watcher[LoanInformationVariables.BorrowerDateOfBirth],
      }),
    );

    if (watcher[LoanInformationVariables.LoanType] === ApplicationType.Joint) {
      dispatch(
        setCoBorrower({
          email: watcher[LoanInformationVariables.CoBorrowerEmail],
          phone: watcher[LoanInformationVariables.CoBorrowerCellPhone],
          dateOfBirth: watcher[LoanInformationVariables.CoBorrowerDateOfBirth],
        }),
      );
    }
  };

  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  const invokeProve = async () => {
    try {
      const authResponse = await proveApi.startInstantAuthentication({
        mobileNumber: removeNonDigits(watcher[LoanInformationVariables.BorrowerCellPhone]),
      });

      if (!!authResponse?.errorStatus) {
        dispatch(
          setProveErrorInfo({
            status: authResponse?.errorStatus,
            description: authResponse?.errorDescription,
          }),
        );
      }

      if (authResponse?.responseType === ProveAuthType.Mobile) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));
        window.location.href = authResponse?.authenticationLink + '&r=t';

        return;
      }

      if (!!authResponse?.sessionId && authResponse?.responseType === ProveAuthType.Instant) {
        dispatch(
          setBorrower({
            firstName: '',
            lastName: '',
            streetNumber: '',
            streetName: '',
            city: '',
            state: '',
            zip: '',
            county: '',
            personalIdNumber: '',
            possessionCheck: {},
          }),
        );
        dispatch(setSessionId(authResponse?.sessionId));
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerPhoneVerification));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      } else {
        dispatch(setIsBorrowerManualFlow(true));
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    } catch (err) {
      dispatch(setIsBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const checkBorrowersPhonesLineType = async () => {
    const phoneLineTypes = await proveApi.getPhoneNumberLineType({
      borrowerPhoneNumber: removeNonDigits(watcher[LoanInformationVariables.BorrowerCellPhone]),
      coborrowerPhoneNumber: removeNonDigits(watcher[LoanInformationVariables.CoBorrowerCellPhone]),
    });

    let phoneVerificationPassed = true;

    if (phoneLineTypes.borrowerLineType !== PhoneLineType.Mobile) {
      phoneVerificationPassed = false;

      setError(LoanInformationVariables.BorrowerCellPhone, {
        type: 'manual',
        message: INVALID_PHONE_LINE_TYPE,
      });
    }

    if (phoneLineTypes.coborrowerLineType && phoneLineTypes.coborrowerLineType !== PhoneLineType.Mobile) {
      phoneVerificationPassed = false;

      setError(LoanInformationVariables.CoBorrowerCellPhone, {
        type: 'manual',
        message: INVALID_PHONE_LINE_TYPE,
      });
    }

    return phoneVerificationPassed;
  };

  const validateLoanForm = handleSubmit(async () => {
    try {
      setButtonLoading(true);

      const phoneVerificationPassed = await checkBorrowersPhonesLineType();

      setButtonLoading(false);

      if (!phoneVerificationPassed) {
        return;
      }
    } catch (error) {
      setButtonLoading(false);

      // if prove return error during checking phone line type we don't stop the flow
    }

    const result = await trigger();

    if (result) {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      saveInformationToStore();

      if (isBorrowerAuthorized) {
        try {
          const { borrowerApplication, hasCobborowerApplication } = await applicationApi.getActiveApplication();

          if (!!borrowerState.isLocked && !borrowerApplication && !hasCobborowerApplication) {
            navigator(AppRoutes.Prequalification);
            dispatch(setPreQualificationStep(PreQualificationStep.NotAbleToAccept));

            return;
          }

          checkBorrower({
            borrowerApplication: borrowerApplication,
            hasCobborowerApplication: hasCobborowerApplication,
            dispatch: dispatch,
            navigator: navigator,
            nextStep: PreQualificationStep.BorrowerInfo,
          });
        } catch (error: any) {
          dispatch(setPreQualificationStep(PreQualificationStep.NotFoundContact));
        } finally {
          dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
        }

        return;
      }

      try {
        const borrower = await dispatch(
          getBorrowerShortInfo({ email: watcher[LoanInformationVariables.BorrowerEmail] }),
        ).unwrap();

        if (borrower && borrower.accountEmail) {
          dispatch(setAccount({ email: borrower.accountEmail }));
          dispatch(setSignInStep(SignInStep.SignIn));
          navigator(AppRoutes.Login);
        }

        if (IS_PROVE_PREFILL_ENABLED) {
          await invokeProve();
        } else {
          dispatch(setIsBorrowerManualFlow(true));
          dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
        }
      } catch (error) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  });

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.LoanAmount));
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>Inquiry Does NOT Affect Your Credit Scores</div>
          </div>
          <FormHeader title="Loan Information" percent={11} />
          <FormBody formBodyClassName={styles.formBody}>
            <div className={styles.rowContainer}>
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.LoanPurpose, event.value as LoanPurpose);
                  trigger(LoanInformationVariables.LoanPurpose);
                }}
                name={LoanInformationVariables.LoanPurpose}
                label={LoanInformationVariables.LoanPurpose}
                options={getSelectOptions(LoanPurpose)}
                value={getSelectOptions(LoanPurpose).find(
                  (option: OptionType) => watcher[LoanInformationVariables.LoanPurpose!] === option.value,
                )}
                placeholder="Select Your Loan Purpose"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.LoanPurpose]?.message}
              />
            </div>
            {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool && (
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.SwimmingPoolType, event.value as SwimmingPoolType);
                  trigger(LoanInformationVariables.SwimmingPoolType);
                }}
                name={LoanInformationVariables.SwimmingPoolType}
                label={LoanInformationVariables.SwimmingPoolType}
                options={getSelectOptions(SwimmingPoolType)}
                value={getSelectOptions(SwimmingPoolType).find(
                  (option: OptionType) => watcher[LoanInformationVariables.SwimmingPoolType!] === option.value,
                )}
                caption="Please Specify Swimming Pool Type"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.SwimmingPoolType]?.message}
              />
            )}
            {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement && (
              <Input
                className={styles.formInput}
                errorMessage={errors[LoanInformationVariables.OtherHomeImprovementType]?.message}
                name={LoanInformationVariables.OtherHomeImprovementType}
                label={LoanInformationVariables.OtherHomeImprovementType}
                onChange={(event) => {
                  let filteredLatinAlphabet = event.target.value.replace(NOT_LATIN_ALPHABET_REG_EX, '');

                  setValue(LoanInformationVariables.OtherHomeImprovementType, filteredLatinAlphabet);
                  trigger(LoanInformationVariables.OtherHomeImprovementType);
                }}
                value={watcher[LoanInformationVariables.OtherHomeImprovementType]}
                caption={OTHER_HOME_IMPROVEMENT_CAPTION}
              />
            )}
            {!isPosLoanAmount && (
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.LoanType, event.value as ApplicationType);
                  trigger(LoanInformationVariables.LoanType);
                }}
                name={LoanInformationVariables.LoanType}
                label={LoanInformationVariables.LoanType}
                options={ApplicationTypeOptions}
                value={ApplicationTypeOptions.find(
                  (option) => watcher[LoanInformationVariables.LoanType!] === option.value,
                )}
                placeholder="Select Your Loan Type"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.LoanType]?.message}
                caption={LOAN_TYPE_CAPTION}
              />
            )}
            {watcher[LoanInformationVariables.LoanType] && (
              <>
                <div className={clsx(styles.rowContainer, styles.blockTitle)}>
                  HFS will use information below to verify your identity and communicate with you.
                </div>
                {watcher[LoanInformationVariables.LoanType] === ApplicationType.Joint && (
                  <div className={clsx(styles.rowContainer, styles.subtitle)}>Borrower Information</div>
                )}
                <div className={styles.rowContainer}>
                  <PhoneNumberInput
                    label={LoanInformationVariables.BorrowerCellPhone}
                    errorMessage={errors[LoanInformationVariables.BorrowerCellPhone]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.BorrowerCellPhone}
                    onChange={(value: string) => {
                      clearErrors([LoanInformationVariables.BorrowerCellPhone]);

                      setValue(LoanInformationVariables.BorrowerCellPhone, value);

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.BorrowerCellPhone);
                      }
                    }}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.BorrowerCellPhone]) {
                        trigger(LoanInformationVariables.BorrowerCellPhone);
                      }
                    }}
                    value={watcher[LoanInformationVariables.BorrowerCellPhone]}
                  />
                  <NumberInput
                    label={LoanInformationVariables.BorrowerDateOfBirth}
                    placeholder="MM/DD/YYYY"
                    errorMessage={errors[LoanInformationVariables.BorrowerDateOfBirth]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.BorrowerDateOfBirth}
                    onChange={(event) => {
                      clearErrors(LoanInformationVariables.BorrowerDateOfBirth);
                      setValue(LoanInformationVariables.BorrowerDateOfBirth, event.target.value);
                    }}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.BorrowerDateOfBirth]) {
                        trigger(LoanInformationVariables.BorrowerDateOfBirth);
                      }
                    }}
                    format="##/##/####"
                    value={watcher[LoanInformationVariables.BorrowerDateOfBirth]}
                    mask="_"
                    allowEmptyFormatting
                  />
                </div>
                <div className={styles.rowContainer}>
                  <Input
                    label={LoanInformationVariables.BorrowerEmail}
                    errorMessage={errors[LoanInformationVariables.BorrowerEmail]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.BorrowerEmail}
                    onChange={(event: any) => {
                      clearErrors([
                        LoanInformationVariables.BorrowerEmail,
                        LoanInformationVariables.ConfirmBorrowerEmail,
                      ]);
                      setValue(LoanInformationVariables.BorrowerEmail, removeSpaces(event.target.value?.toLowerCase()));

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.BorrowerEmail);
                      }
                    }}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.ConfirmBorrowerEmail]) {
                        trigger(LoanInformationVariables.ConfirmBorrowerEmail);
                      }

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.CoBorrowerEmail);
                      }
                    }}
                    onCopy={handleCopyPaste}
                    value={watcher[LoanInformationVariables.BorrowerEmail]}
                    disabled={isBorrowerAuthorized}
                  />
                  {!isBorrowerAuthorized && (
                    <Input
                      label={LoanInformationVariables.ConfirmBorrowerEmail}
                      errorMessage={errors[LoanInformationVariables.ConfirmBorrowerEmail]?.message}
                      className={styles.formInput}
                      name={LoanInformationVariables.ConfirmBorrowerEmail}
                      onChange={(event: any) => {
                        clearErrors([
                          LoanInformationVariables.BorrowerEmail,
                          LoanInformationVariables.ConfirmBorrowerEmail,
                        ]);
                        setValue(
                          LoanInformationVariables.ConfirmBorrowerEmail,
                          removeSpaces(event.target.value?.toLowerCase()),
                        );

                        if (isSubmitted) {
                          trigger(LoanInformationVariables.ConfirmBorrowerEmail);
                        }
                      }}
                      onBlur={() => {
                        clearErrors(LoanInformationVariables.ConfirmBorrowerEmail);

                        if (
                          isSubmitted ||
                          (watcher[LoanInformationVariables.BorrowerEmail] &&
                            watcher[LoanInformationVariables.ConfirmBorrowerEmail])
                        ) {
                          trigger(LoanInformationVariables.ConfirmBorrowerEmail);
                        }
                      }}
                      onPaste={handleCopyPaste}
                      value={watcher[LoanInformationVariables.ConfirmBorrowerEmail]}
                      autoComplete="new-password"
                    />
                  )}
                </div>
              </>
            )}
            {watcher[LoanInformationVariables.LoanType] === ApplicationType.Joint && (
              <>
                <div className={clsx(styles.rowContainer, styles.subtitle)}>Co-borrower Information</div>
                <div className={styles.rowContainer}>
                  <PhoneNumberInput
                    label={LoanInformationVariables.CoBorrowerCellPhone}
                    errorMessage={errors[LoanInformationVariables.CoBorrowerCellPhone]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.CoBorrowerCellPhone}
                    onChange={(value: string) => {
                      clearErrors([LoanInformationVariables.CoBorrowerCellPhone]);
                      setValue(LoanInformationVariables.CoBorrowerCellPhone, value);

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.CoBorrowerCellPhone);
                      }
                    }}
                    value={watcher[LoanInformationVariables.CoBorrowerCellPhone]}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.CoBorrowerCellPhone]) {
                        trigger(LoanInformationVariables.CoBorrowerCellPhone);
                      }
                    }}
                  />
                  <NumberInput
                    label={LoanInformationVariables.CoBorrowerDateOfBirth}
                    placeholder="MM/DD/YYYY"
                    errorMessage={errors[LoanInformationVariables.CoBorrowerDateOfBirth]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.CoBorrowerDateOfBirth}
                    onChange={(event) => {
                      clearErrors(LoanInformationVariables.CoBorrowerDateOfBirth);
                      setValue(LoanInformationVariables.CoBorrowerDateOfBirth, event.target.value);
                    }}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.CoBorrowerDateOfBirth]) {
                        trigger(LoanInformationVariables.CoBorrowerDateOfBirth);
                      }
                    }}
                    format="##/##/####"
                    value={watcher[LoanInformationVariables.CoBorrowerDateOfBirth]}
                    mask="_"
                    allowEmptyFormatting
                  />
                </div>
                <div className={styles.rowContainer}>
                  <Input
                    label={LoanInformationVariables.CoBorrowerEmail}
                    errorMessage={errors[LoanInformationVariables.CoBorrowerEmail]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.CoBorrowerEmail}
                    onChange={(event: any) => {
                      clearErrors([
                        LoanInformationVariables.CoBorrowerEmail,
                        LoanInformationVariables.ConfirmCoBorrowerEmail,
                      ]);
                      setValue(
                        LoanInformationVariables.CoBorrowerEmail,
                        removeSpaces(event.target.value?.toLowerCase()),
                      );

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.CoBorrowerEmail);
                      }
                    }}
                    onBlur={() => {
                      if (watcher[LoanInformationVariables.ConfirmCoBorrowerEmail]) {
                        trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                      }
                    }}
                    value={watcher[LoanInformationVariables.CoBorrowerEmail]}
                    onCopy={handleCopyPaste}
                  />
                  <Input
                    label={LoanInformationVariables.ConfirmCoBorrowerEmail}
                    errorMessage={errors[LoanInformationVariables.ConfirmCoBorrowerEmail]?.message}
                    className={styles.formInput}
                    name={LoanInformationVariables.ConfirmCoBorrowerEmail}
                    onChange={(event: any) => {
                      clearErrors([
                        LoanInformationVariables.CoBorrowerEmail,
                        LoanInformationVariables.ConfirmCoBorrowerEmail,
                      ]);
                      setValue(
                        LoanInformationVariables.ConfirmCoBorrowerEmail,
                        removeSpaces(event.target.value?.toLowerCase()),
                      );

                      if (isSubmitted) {
                        trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                      }
                    }}
                    onBlur={() => {
                      clearErrors(LoanInformationVariables.ConfirmCoBorrowerEmail);

                      if (
                        isSubmitted ||
                        (watcher[LoanInformationVariables.CoBorrowerEmail] &&
                          watcher[LoanInformationVariables.ConfirmCoBorrowerEmail])
                      ) {
                        trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                      }
                    }}
                    onPaste={handleCopyPaste}
                    value={watcher[LoanInformationVariables.ConfirmCoBorrowerEmail]}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={validateLoanForm} withArrow isLoading={buttonLoading}>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default LoanInformation;
